/**
 * Connectors Page (Track 3 S4)
 *
 * Tenant self-serve connector management over the connector registry:
 * connect (popup-based OAuth or paste-key), disconnect, re-auth, credential
 * badges, and per-connector sync state. Extends the sync-health surface
 * (SyncHealth.tsx) with the write half.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Modal,
  TextField,
  Box,
  List,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import {
  listConnectors,
  getAuthorizeUrl,
  connectWithKey,
  disconnectConnector,
  type ConnectorSummary,
} from '../services/connectorsApi';
import { startShopifyInstall } from '../services/shopifyAuthApi';

/** Human-readable messages for /api/auth/callback error codes. */
const SHOPIFY_ERROR_MESSAGES: Record<string, string> = {
  invalid_hmac: 'Shopify returned an unverifiable response. Please retry.',
  invalid_shop: 'That shop domain is not a valid myshopify.com domain.',
  invalid_state: 'The install link expired or was tampered with. Please retry.',
  unknown_state: 'The install link was already used or expired. Please retry.',
  state_expired: 'The install link expired. Please retry.',
  missing_code: 'Shopify did not complete the authorization. Please retry.',
  token_exchange_failed:
    'Could not obtain access from Shopify. Please retry.',
  store_already_claimed:
    'This store is already connected to another workspace.',
  install_failed: 'The install could not be saved. Please retry.',
};

/** Paste-key field sets per connector (account ids are NEVER asked for —
 * the backend derives them from the credential itself: for Shopify that's
 * the shop's own myshopify_domain, corroborated by an authenticated call
 * to the shop's Admin API, not the shop_domain field taken at face value). */
const PASTE_KEY_FIELDS: Record<string, { key: string; label: string; secret?: boolean }[]> = {
  twilio_sms: [
    { key: 'account_sid', label: 'Account SID' },
    { key: 'auth_token', label: 'Auth Token', secret: true },
  ],
  outbound_sequence: [{ key: 'api_key', label: 'API Key', secret: true }],
  klaviyo: [{ key: 'api_key', label: 'Private API Key', secret: true }],
  hubspot: [{ key: 'access_token', label: 'Private App Token', secret: true }],
  shopify: [
    { key: 'shop_domain', label: 'Shop domain (e.g. my-store.myshopify.com)' },
    { key: 'access_token', label: 'Admin API access token', secret: true },
  ],
};

/** Guided steps shown above the field set for connectors whose token isn't
 * self-explanatory. Shopify has no OAuth app to install here (T26 front-door
 * decision pending) — a merchant creates a custom app in their own admin. */
const PASTE_KEY_GUIDANCE: Record<string, string[]> = {
  shopify: [
    'In your Shopify admin, go to Settings > Apps and sales channels > Develop apps, then click "Create an app".',
    'Under Configuration, set Admin API scopes to include read_orders, read_products, read_customers, and read_analytics.',
    'Click Install app, then confirm the install.',
    'Under API credentials, reveal and copy the Admin API access token — paste it below along with your shop domain.',
  ],
};

function credentialBadge(status: string | null): { tone: any; label: string } {
  switch (status) {
    case 'active':
      return { tone: 'success', label: 'Credentials active' };
    case 'expired':
      return { tone: 'warning', label: 'Re-auth needed' };
    case 'revoked':
      return { tone: 'critical', label: 'Revoked' };
    case 'invalid':
      return { tone: 'critical', label: 'Invalid' };
    default:
      return { tone: 'info', label: 'Not connected' };
  }
}

const Connectors: React.FC = () => {
  const [connectors, setConnectors] = useState<ConnectorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Paste-key modal state
  const [keyModalConnector, setKeyModalConnector] = useState<ConnectorSummary | null>(null);
  const [keyValues, setKeyValues] = useState<Record<string, string>>({});

  // Shopify store install (top-level OAuth redirect, not a popup)
  const [shopDomain, setShopDomain] = useState('');
  const [shopBusy, setShopBusy] = useState(false);

  const popupRef = useRef<Window | null>(null);

  // Surface the install callback's outcome (?shopify= / ?shopify_error=).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('shopify') === 'connected') {
      setNotice('Shopify store connected. First data sync is starting.');
    }
    const errorCode = params.get('shopify_error');
    if (errorCode) {
      setError(
        SHOPIFY_ERROR_MESSAGES[errorCode] ||
          `Shopify connection failed (${errorCode})`
      );
    }
    if (params.get('shopify') || errorCode) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectShopify = async () => {
    setShopBusy(true);
    setError(null);
    setNotice(null);
    try {
      const url = await startShopifyInstall(shopDomain);
      window.location.assign(url);
    } catch (e: any) {
      setError(e.message || 'Failed to start the Shopify install');
      setShopBusy(false);
    }
  };

  const loadData = useCallback(async () => {
    setError(null);
    try {
      const data = await listConnectors();
      setConnectors(data.connectors);
    } catch (e: any) {
      setError(e.message || 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // The OAuth popup posts {type: 'oauth_result', ok} when the backend
  // callback completes; refresh the list on success.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'oauth_result') {
        if (event.data.ok) {
          setNotice('Connection complete.');
          loadData();
        } else {
          setError(`Connection failed (${event.data.detail || 'unknown'})`);
        }
        popupRef.current = null;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [loadData]);

  const startOAuth = async (connector: ConnectorSummary) => {
    setBusyKey(connector.connector_key);
    setError(null);
    setNotice(null);
    try {
      const url = await getAuthorizeUrl(connector.connector_key);
      popupRef.current = window.open(
        url,
        'oauth_connect',
        'width=600,height=750,menubar=no,toolbar=no'
      );
    } catch (e: any) {
      setError(e.message || 'Failed to start authorization');
    } finally {
      setBusyKey(null);
    }
  };

  const openKeyModal = (connector: ConnectorSummary) => {
    setKeyValues({});
    setKeyModalConnector(connector);
  };

  const submitKeyConnect = async () => {
    if (!keyModalConnector) return;
    setBusyKey(keyModalConnector.connector_key);
    setError(null);
    setNotice(null);
    try {
      await connectWithKey(keyModalConnector.connector_key, keyValues);
      setNotice('Connection complete.');
      setKeyModalConnector(null);
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to connect');
    } finally {
      setBusyKey(null);
    }
  };

  const doDisconnect = async (connector: ConnectorSummary, connectionId: string) => {
    setBusyKey(connector.connector_key);
    setError(null);
    setNotice(null);
    try {
      await disconnectConnector(connector.connector_key, connectionId);
      setNotice('Disconnected. Credentials revoked and remote sync disabled.');
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to disconnect');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Connectors">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  const keyFields = keyModalConnector
    ? PASTE_KEY_FIELDS[keyModalConnector.connector_key] || [
        { key: 'api_key', label: 'API Key', secret: true },
      ]
    : [];
  const keyGuidance = keyModalConnector
    ? PASTE_KEY_GUIDANCE[keyModalConnector.connector_key]
    : undefined;

  return (
    <Page
      title="Connections"
      subtitle="Connect the tools your marketing and sales data lives in"
      primaryAction={{
        content: 'Refresh',
        icon: RefreshIcon,
        onAction: () => loadData(),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {notice && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setNotice(null)}>
              {notice}
            </Banner>
          </Layout.Section>
        )}
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">
                    Shopify store
                  </Text>
                  <Badge tone="info">Required</Badge>
                </InlineStack>
                <Text as="p" tone="subdued">
                  Install the app on your Shopify store to start syncing
                  orders, products, and customers. You'll approve access on
                  Shopify and come right back here.
                </Text>
                <InlineStack gap="300" blockAlign="end" wrap={false}>
                  <div style={{ flexGrow: 1 }}>
                    <TextField
                      label="Store domain"
                      labelHidden
                      placeholder="mystore.myshopify.com"
                      value={shopDomain}
                      onChange={setShopDomain}
                      autoComplete="off"
                      disabled={shopBusy}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={connectShopify}
                    loading={shopBusy}
                    disabled={!shopDomain.trim()}
                  >
                    Connect store
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
            {connectors.map((connector) => {
              const badge = credentialBadge(connector.credential_status);
              const enabledConnections = connector.connections.filter(
                (c) => c.is_enabled
              );
              const needsReauth = connector.credential_status === 'expired';
              return (
                <Card key={connector.connector_key}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="300" blockAlign="center">
                        <Text as="h3" variant="headingMd">
                          {connector.display_name}
                        </Text>
                        <Badge tone={connector.is_connected ? 'success' : undefined}>
                          {connector.is_connected ? 'Connected' : 'Not connected'}
                        </Badge>
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                        {connector.health_status && (
                          <Badge
                            tone={
                              connector.health_status === 'healthy'
                                ? 'success'
                                : connector.health_status === 'delayed'
                                  ? 'warning'
                                  : 'critical'
                            }
                          >
                            {`Sync ${connector.health_status}`}
                          </Badge>
                        )}
                      </InlineStack>
                      <InlineStack gap="200">
                        {connector.oauth_available && (
                          <Button
                            loading={busyKey === connector.connector_key}
                            onClick={() => startOAuth(connector)}
                          >
                            {needsReauth
                              ? 'Re-authorize'
                              : connector.is_connected
                                ? 'Add account'
                                : 'Connect'}
                          </Button>
                        )}
                        {connector.auth_kind === 'api_key' ||
                        (!connector.oauth_available &&
                          connector.auth_kind === 'oauth2') ? (
                          <Button
                            variant={connector.oauth_available ? 'tertiary' : undefined}
                            loading={busyKey === connector.connector_key}
                            onClick={() => openKeyModal(connector)}
                          >
                            {connector.auth_kind === 'api_key'
                              ? 'Connect with key'
                              : 'Connect with token'}
                          </Button>
                        ) : null}
                      </InlineStack>
                    </InlineStack>
                    {enabledConnections.length > 0 && (
                      <BlockStack gap="200">
                        {enabledConnections.map((c) => (
                          <Box
                            key={c.connection_id}
                            padding="300"
                            background="bg-surface-secondary"
                            borderRadius="200"
                          >
                            <InlineStack align="space-between" blockAlign="center">
                              <BlockStack gap="050">
                                <Text as="p" variant="bodyMd">
                                  {c.external_account_id || 'Account pending mapping'}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {c.last_sync_at
                                    ? `Last synced ${new Date(c.last_sync_at).toLocaleString()}`
                                    : 'Never synced'}
                                </Text>
                              </BlockStack>
                              <Button
                                tone="critical"
                                variant="tertiary"
                                loading={busyKey === connector.connector_key}
                                onClick={() => doDisconnect(connector, c.connection_id)}
                              >
                                Disconnect
                              </Button>
                            </InlineStack>
                          </Box>
                        ))}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              );
            })}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={keyModalConnector !== null}
        onClose={() => setKeyModalConnector(null)}
        title={`Connect ${keyModalConnector?.display_name ?? ''}`}
        primaryAction={{
          content: 'Connect',
          onAction: submitKeyConnect,
          loading: busyKey === keyModalConnector?.connector_key,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setKeyModalConnector(null) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              Paste the credential from your {keyModalConnector?.display_name}{' '}
              account. The connected account is verified and derived from the
              credential itself.
            </Text>
            {keyGuidance && (
              <List type="number">
                {keyGuidance.map((step) => (
                  <List.Item key={step}>{step}</List.Item>
                ))}
              </List>
            )}
            {keyFields.map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                type={field.secret ? 'password' : 'text'}
                value={keyValues[field.key] || ''}
                onChange={(value) =>
                  setKeyValues((prev) => ({ ...prev, [field.key]: value }))
                }
                autoComplete="off"
              />
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Connectors;
